import React, {FunctionComponent, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    Container,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Typography
} from "@mui/material";
import './stylesTrackers.css';
import {Period, Skeleton} from "@atiautomacao/ati-ui-library";
import {useDispatch} from "react-redux";
import {getNcus, isSameTrackerPeriod, normalizeNcuDayPeriod, setPeriodTrackers} from "./TrackersReducer";
import {useAppSelector} from "../../../../Config/Hooks";
import {AppDispatch, RootState} from "../../../../Config/Store";
import Box from "@mui/material/Box";
import {useSnackbar} from "notistack";
import {CommandDTO, SendingTelecommandUnifilar} from "../../../../Shared/Components/SendingTelecommandUnifilar";
import {findAllCommands} from "../../../../Utils/CommandService"
import TrackerAvailabilityChart from "./TrackerAvailabilityChart";
import DataNotFound from "../../../../Shared/Components/DataNotFoundMessage";
import NcuPositionHistoryCard, {TrackerOption, TrackerPositionViewMode} from "./NcuPositionHistoryCard";
import {createTrackerRequestLimiter} from "./trackerRequestLimiter";
import {DashboardNavigationMenu} from "../../../../Shared/Components/Layout/menu/types/DashboardNavigationMenu";

const PAGE_TITLE = "Acompanhamento dos Trackers";
const POSITION_HISTORY_TITLE = "Histórico de Posicionamento";
export const ALL_NCUS = "all";

type NcuSelection = number | typeof ALL_NCUS;

const defaultNcuPeriod = (): Period => ({
    groupBy: "day",
    fromDateTime: new Date(),
    toDateTime: new Date()
});

interface TrackerLoadCycle {
    powerPlantId: number;
    loadRequestId: string;
}

const sortTrackerOptions = (options: TrackerOption[]) => [...options].sort((left, right) => {
    const byName = left.name.localeCompare(right.name, undefined, {numeric: true, sensitivity: "base"});
    return byName !== 0 ? byName : left.id - right.id;
});

const findNavigationEquipmentName = (powerStation: DashboardNavigationMenu | null, id: number) => {
    const equipment = powerStation?.equipmentList?.find(item => item.id === id) ??
        powerStation?.skidList?.flatMap(skid => skid.equipmentList ?? []).find(item => item.id === id);
    const name = equipment?.name;
    return typeof name === "string" && name.trim() ? name.trim() : undefined;
};

const buildTrackerOptions = (ids: number[], powerStation: DashboardNavigationMenu | null) => sortTrackerOptions(
    ids.map((id): TrackerOption => {
        const navigationName = findNavigationEquipmentName(powerStation, id);
        return {
            id,
            name: navigationName ?? `NCU ${id}`,
            nameSource: navigationName ? "navigation" : "fallback"
        };
    })
);

const TrackersPage: FunctionComponent<{powerPlantId: number}> = ({powerPlantId}) => {
    const { enqueueSnackbar } = useSnackbar();
    const {periodTrackers} = useAppSelector((state: RootState) => state.trackers);
    const dispatch = useDispatch<AppDispatch>();
    const [ncuIds, setNcuIds] = useState<number[]>([]);
    const [selection, setSelection] = useState<NcuSelection | null>(null);
    const [listOwner, setListOwner] = useState<TrackerLoadCycle | null>(null);
    const [listStatus, setListStatus] = useState<"loading" | "ready" | "error">("loading");
    const [individualPositionMode, setIndividualPositionMode] = useState<TrackerPositionViewMode>("curves");
    const [allModePeriods, setAllModePeriods] = useState<Record<number, Period>>({});
    const [allModeDefaultPeriod, setAllModeDefaultPeriod] = useState<Period>(defaultNcuPeriod);
    const { entities } = useAppSelector((state: RootState) => state.dashboardNavigationMenu);
    const [commandDTOList, setCommandDTOList] = useState<CommandDTO[]>([])
    const [open, setOpen] = useState(false)
    const [equipmentId, setEquipmentId] = useState(-1)
    const requestLimiter = useMemo(() => createTrackerRequestLimiter(), []);

    const powerStationEntity = useMemo(
        () => entities.find(entity => entity.id === powerPlantId) ?? null,
        [entities, powerPlantId]
    );
    const powerStationEntityRef = useRef(powerStationEntity);
    powerStationEntityRef.current = powerStationEntity;
    const trackerOptions = useMemo(
        () => buildTrackerOptions(ncuIds, powerStationEntity),
        [ncuIds, powerStationEntity]
    );
    const individualPeriod = useMemo(
        () => normalizeNcuDayPeriod(periodTrackers ?? defaultNcuPeriod()),
        [periodTrackers]
    );
    const activeLoadCycleRef = useRef<TrackerLoadCycle | null>(null);
    const hasCurrentNcuList = listOwner?.powerPlantId === powerPlantId &&
        listOwner.loadRequestId === activeLoadCycleRef.current?.loadRequestId;
    const visibleTrackerOptions = useMemo(
        () => hasCurrentNcuList ? trackerOptions : [],
        [hasCurrentNcuList, trackerOptions]
    );
    const showAllOption = visibleTrackerOptions.length > 1;
    const isAllSelected = selection === ALL_NCUS && showAllOption;
    const selectedTrackerOption = visibleTrackerOptions.find(option => option.id === selection) ?? null;

    useEffect(() => {
        setNcuIds([]);
        setSelection(null);
        setListOwner(null);
        setListStatus("loading");
        setAllModePeriods({});
        setAllModeDefaultPeriod(defaultNcuPeriod());
        dispatch(setPeriodTrackers(defaultNcuPeriod()));
        const request = dispatch(getNcus(powerPlantId));
        const loadCycle = {powerPlantId, loadRequestId: request.requestId};
        activeLoadCycleRef.current = loadCycle;

        request.then((action) => {
            const activeCycle = activeLoadCycleRef.current;
            if (activeCycle?.powerPlantId !== loadCycle.powerPlantId ||
                activeCycle.loadRequestId !== loadCycle.loadRequestId) {
                return;
            }

            setListOwner(loadCycle);
            if (getNcus.fulfilled.match(action)) {
                const ids = Array.isArray(action.payload?.data?.data) ? action.payload.data.data : [];
                const uniqueIds = Array.from(new Set<number>(ids.map(Number).filter(Number.isFinite)));
                const options = buildTrackerOptions(uniqueIds, powerStationEntityRef.current);
                setNcuIds(uniqueIds);
                setSelection(options[0]?.id ?? null);
                setListStatus("ready");
            } else {
                setNcuIds([]);
                setSelection(null);
                setListStatus("error");
            }
        });

        return () => {
            if (activeLoadCycleRef.current?.loadRequestId === loadCycle.loadRequestId) {
                activeLoadCycleRef.current = null;
            }
            request.abort?.();
        };
    }, [dispatch, powerPlantId]);

    const handleIndividualPeriodChange = useCallback((newPeriod: Period) => {
        const normalized = normalizeNcuDayPeriod(newPeriod);
        if (isSameTrackerPeriod(periodTrackers, normalized)) {
            return;
        }
        dispatch(setPeriodTrackers(normalized));
    }, [dispatch, periodTrackers]);

    const handleAllModePeriodChange = useCallback((ncuId: number, newPeriod: Period) => {
        setAllModePeriods(current => ({...current, [ncuId]: newPeriod}));
    }, []);

    const allModePeriodHandlers = useMemo(() => (
        Object.fromEntries(visibleTrackerOptions.map(option => [
            option.id,
            (newPeriod: Period) => handleAllModePeriodChange(option.id, newPeriod)
        ]))
    ), [handleAllModePeriodChange, visibleTrackerOptions]);

    const handleCloseModal = () => {
        setOpen(false)
        setCommandDTOList([]);
        setEquipmentId(-1)
    };

    // Ao trocar de usina, descarta o modal e a busca de comandos da usina anterior.
    useEffect(() => {
        setOpen(false);
        setCommandDTOList([]);
        setEquipmentId(-1);
    }, [powerPlantId]);

    useEffect(() => {
        if (equipmentId === -1) {
            setCommandDTOList([]);
            return;
        }
        // Só a busca do equipamento atual pode alterar o modal: respostas de cliques
        // anteriores (ex.: outra NCU na visão "Todos") chegam fora de ordem e são ignoradas.
        let active = true;
        findAllCommands(equipmentId).then(
            (response:any) => {
                if (!active) {
                    return;
                }
                const commands = response?.data?.data;
                if (Array.isArray(commands) && commands.length > 0) {
                    setCommandDTOList(commands);
                    return;
                }
                handleCloseModal();
                enqueueSnackbar("Comandos não encontrados!", {variant: "error", autoHideDuration: 2000});
            }
        ).catch(
            () => {
                if (!active) {
                    return;
                }
                handleCloseModal();
                enqueueSnackbar("Comandos não encontrados!", { variant: "error",autoHideDuration: 2000 });
        })
        return () => {
            active = false;
        };
    }, [equipmentId, enqueueSnackbar]);

    const toOpen = useCallback((id: number) => {
        setEquipmentId(id)
        setOpen(true)
    }, []);

    const cardAriaLabel = (option: TrackerOption) => `${powerStationEntity?.name ?? "Usina"} - ${option.name}`;

    const skeletonToRender = <Skeleton animation="wave" variant="rounded" height={800} width="100%" />;
    const emptyState = (
        <Container fixed>
            <Box sx={{bgcolor: '#e1e0e0', height: '500px', width: '100%'}}>
                <DataNotFound
                    boxStyle={{height: '500px', width: '100%'}}
                    titleStyle={{fontSize: '23px', color: '#646363'}}
                />
            </Box>
        </Container>
    );
    const errorState = (
        <Container fixed>
            <Box sx={{bgcolor: '#e1e0e0', height: '500px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <Typography sx={{fontSize: '23px', color: '#646363'}}>
                    Não foi possível carregar os dados.
                </Typography>
            </Box>
        </Container>
    );

    const allNcusToRender = listOwner && (
        <Box className="tracker-position-all-ncus">
            {visibleTrackerOptions.map(option => (
                <NcuPositionHistoryCard
                    key={`${powerPlantId}-${listOwner.loadRequestId}-${option.id}`}
                    powerPlantId={powerPlantId}
                    ncu={option}
                    title={option.name}
                    modalTitle={`${POSITION_HISTORY_TITLE} - ${option.name}`}
                    ariaLabel={cardAriaLabel(option)}
                    period={allModePeriods[option.id] ?? allModeDefaultPeriod}
                    onPeriodChange={allModePeriodHandlers[option.id]}
                    onOpenCommands={toOpen}
                    requestLimiter={requestLimiter}
                />
            ))}
        </Box>
    );

    const individualNcuToRender = listOwner && selectedTrackerOption && (
        <NcuPositionHistoryCard
            key={`${powerPlantId}-${listOwner.loadRequestId}`}
            powerPlantId={powerPlantId}
            ncu={selectedTrackerOption}
            title={selectedTrackerOption.name}
            modalTitle={`${POSITION_HISTORY_TITLE} - ${selectedTrackerOption.name}`}
            ariaLabel={cardAriaLabel(selectedTrackerOption)}
            period={individualPeriod}
            onPeriodChange={handleIndividualPeriodChange}
            onOpenCommands={toOpen}
            requestLimiter={requestLimiter}
            positionMode={individualPositionMode}
            onPositionModeChange={setIndividualPositionMode}
        />
    );

    const positionChartToRender = !hasCurrentNcuList || listStatus === "loading"
        ? skeletonToRender
        : listStatus === "error"
            ? errorState
            : visibleTrackerOptions.length === 0
                ? emptyState
                : isAllSelected
                    ? allNcusToRender
                    : individualNcuToRender;

    const selectValue = isAllSelected ? ALL_NCUS : selectedTrackerOption ? selectedTrackerOption.id : '';

    return (
        <>
        <Grid>
            <Box className="tracker-page-section-header tracker-page-header">
                <Typography component="h1" className="tracker-page-title" color="text.primary">
                    {PAGE_TITLE}
                </Typography>
                <Divider/>
            </Box>
            <FormControl sx={{m: 1, mb: 3, width: '100%'}}>
                <InputLabel id="ncu-checkbox-label">Seleção de NCU</InputLabel>
                <Select
                    labelId="ncu-checkbox-label"
                    id="ncu-checkbox"
                    label="Seleção de NCU"
                    value={selectValue}
                    onChange={(e) => {
                        if (e.target.value === ALL_NCUS) {
                            setSelection(showAllOption ? ALL_NCUS : visibleTrackerOptions[0]?.id ?? null);
                            return;
                        }
                        const selectedId = Number(e.target.value);
                        setSelection(visibleTrackerOptions.some(option => option.id === selectedId) ? selectedId : null);
                    }}
                >
                    {visibleTrackerOptions.map((ncu) => (
                        <MenuItem key={ncu.id} value={ncu.id}
                            color={"default"}
                        >{ncu.name}</MenuItem>
                    ))}
                    {showAllOption && (
                        <MenuItem key={ALL_NCUS} value={ALL_NCUS}>Todos</MenuItem>
                    )}
                </Select>
            </FormControl>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <TrackerAvailabilityChart powerPlantId={powerPlantId}/>
                <Box className="tracker-page-section-header">
                    <Typography component="h2" className="tracker-page-title" color="text.primary">
                        {POSITION_HISTORY_TITLE} por NCU
                    </Typography>
                    <Divider/>
                </Box>
                {positionChartToRender}
            </Box>
        </Grid>
        {commandDTOList.length > 0 &&
        <SendingTelecommandUnifilar
            open={open}
            onCloseModal={handleCloseModal}
            commandDTOList={commandDTOList}
        />
        }
        </>
    )
}

export default TrackersPage;
